import type { AssetCategory, ContentType } from "@/Dashboard/pages/AssetLibrary/types";
import type { MenuItem } from "@/VisualBuilder/components/LeftHandMenu/types.js";
import { EXPERIENCE_PRIMARY_FILTER, TAB_STATUSES, VB_MODE } from "@/constants";
import type { ThunkDispatch } from "@/store/hooks";
import { currentPropertySelector } from "@/store/selectors";
import type { Asset, State, Tag, VisualBuilderDataState } from "@/store/types.js";
import { updateVisualBuilderData } from "@/store/v2/visualBuilderData/actions.js";
import {
  type VisualBuilderDataSelectorParams,
  visualBuilderDataSelector,
} from "@/store/v2/visualBuilderData/selectors";
import type { VisualBuilderModeResult } from "@/store/v2/visualBuilderData/types";
import {
  getDrawerOptions,
  getSortedContentTypes,
  getSortedMediaItems,
  getSortedTags,
  getVisualBuilderFilteredContentTypes,
  getVisualBuilderFilteredMediaItems,
  getVisualBuilderFilteredTags,
  getVisualBuilderMode,
  transformContentTypesData,
  transformMediaItemData,
  transformTagsData,
} from "@/utils/visualBuilder.js";

import { sortMenuItems } from "../drawerUtils";

/**
 * Initial state for the visual builder data.
 * Contains default values for filters, drawer states, views, and other UI configurations.
 */
export const visualBuilderDataInitialState: VisualBuilderDataState = {
  // Filter-related defaults
  primaryFilter: EXPERIENCE_PRIMARY_FILTER.TAG,
  typeFilterId: undefined,
  tagFilterId: undefined,

  // Drawer visibility settings - note special handling for shared URLs
  leftDrawer: true,
  leftDrawerAutoHide: window.location.search.includes("shareId="),
  rightDrawer: true,
  rightDrawerAutoHide: false,
  gridView: false,

  // UI state configurations
  tagsToggled: {},
  carousel: false,
  typeTagFilter: false,
  searchField: false,
  currentMediaItemId: undefined,

  // Data containers
  search: {},
  contentTypes: [] as ContentType[],
  filteredContentTypes: [],
  tags: [] as Tag[],
  filteredTags: [],
  mediaItems: [] as Asset[],
  filteredMediaItems: [],

  // Relationship mappings
  contentTypeTags: {},
  tagContentTypes: {},
  validContentTypes: {},
  validTags: {},
  tagMediaItems: {},
  filteredTagMediaItems: {},

  // State flags and ordering
  searchPerformed: false,
  contentsOrdered: {},
  experienceContentTypeIds: {},
  experienceTagIds: {},
  contentTypesOrdered: {},
  tagsOrdered: {},
  hiddenContentTypes: {},
  hiddenTags: {},
  locked: false,
  shouldToggleTagItem: true,

  // Asset-related state
  assetCategories: [] as AssetCategory[],
  assetCategoriesOrdered: {},
  assetCategoriesToggled: {},
  assetCategoryContentTypes: {},
  assetCategoryFilterId: undefined,
  assetCategoryMediaItems: {},
  experienceAssetCategoryIds: {},
  filteredAssetCategories: [],
  filteredAssetCategoryMediaItems: {},
  hiddenAssetCategories: {},
  typesToggled: {},

  assetCategoryNames: {},
  tagNames: {},
  id: undefined,
  experienceAssets: [],
  propertyId: -1,
};

type Payload = typeof visualBuilderDataInitialState & {
  assetCategoryTabData?: {
    mediaItems: Asset[];
    assetCategoryHiddenMediaItems: Asset[];
    contentTypeHiddenMediaItems: Asset[];
  };
  contentTypeTabData?: {
    mediaItems: Asset[];
    assetCategoryHiddenMediaItems: Asset[];
    contentTypeHiddenMediaItems: Asset[];
  };
};

/**
 * Action creator to set initial values for presentation mode
 * @param {Object} params - Configuration parameters including tabStatus
 * @param {Object} data - Initial visual data needed for logic
 * @returns {Function} Thunk action function
 */
export function addPresentationInitialValues(
  params: VisualBuilderDataSelectorParams & {
    propertyMenuItems?: MenuItem[];
  },
  data: {
    mediaItems?: Asset[];
    contentTypes?: ContentType[];
    tags?: Tag[];
    filteredAssetCategories?: AssetCategory[];
  } = {},
): (dispatch: ThunkDispatch, getState: () => State) => Promise<void> {
  return async (dispatch: ThunkDispatch) => {
    const { tabStatus, propertyMenuItems = [] } = params;
    const { mode } = getVisualBuilderMode(params) as VisualBuilderModeResult;

    const { mediaItems = [], contentTypes = [], tags = [], filteredAssetCategories = [] } = data;

    const { initialCategory, initialContentType, initialMediaItem } = getInitialPresentationSelection({
      mediaItems,
      contentTypes,
      assetCategories: filteredAssetCategories,
      propertyMenuItems,
    });

    // Only proceed if in presentation mode
    if (mode !== VB_MODE.PRESENTATION) {
      return;
    }

    // Set initial values for homebase and collect tabs if media items exist
    if ((tabStatus === TAB_STATUSES.HOMEBASE || tabStatus === TAB_STATUSES.COLLECT) && mediaItems.length) {
      const values: {
        gridView: boolean;
        typeFilterId?: number;
        tagFilterId?: number;
        assetCategoryFilterId?: number;
        currentMediaItemId?: number;
      } = { gridView: false };

      if (initialContentType) {
        values.typeFilterId = initialContentType.id;
      }

      if (values.typeFilterId) {
        const initialTag = tags.find(
          (tag) => values.typeFilterId && tag.contentTypes?.includes(values.typeFilterId) && !tag.hidden,
        );
        if (initialTag) {
          values.tagFilterId = initialTag.id;
        }
      }

      // Set initial asset category
      if (values.typeFilterId) {
        if (initialCategory) {
          values.assetCategoryFilterId = initialCategory.id;
        }
      }

      // Set initial media item based on selected filters
      if (values.typeFilterId && values.tagFilterId) {
        if (initialMediaItem) {
          values.currentMediaItemId = initialMediaItem.id;
        }
      }

      dispatch(updateVisualBuilderData(params, values, {}, true));
    }
  };
}

/**
 * Action creator to set initial values for visualization mode
 * @param {Object} params - Configuration parameters
 * @returns {Function} Thunk action function
 */
export function addVisualizationInitialValues(
  params: VisualBuilderDataSelectorParams,
): (dispatch: ThunkDispatch, getState: () => State) => Promise<void> {
  return async (dispatch: ThunkDispatch, getState: () => State) => {
    const { tabStatus } = params;
    const { mode } = getVisualBuilderMode(params) as VisualBuilderModeResult;
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    const { filteredMediaItems = [], gridView } = visualBuilderData;

    // Only proceed for visualization mode
    if (mode !== VB_MODE.VISUALIZATION) {
      return;
    }

    // Set initial media item for customize tab
    if (tabStatus === TAB_STATUSES.CUSTOMIZE && filteredMediaItems.length && !gridView) {
      const values: {
        currentMediaItemId?: number;
      } = {};
      const initialMediaItem = filteredMediaItems[0];

      if (initialMediaItem) {
        values.currentMediaItemId = initialMediaItem.id;
      }

      if (Object.keys(values).length) {
        dispatch(updateVisualBuilderData(params, values));
      }
    }
  };
}

/**
 * Action creator to set initial values for public visualization
 * @param {Object} params - Configuration parameters
 * @returns {Function} Thunk action function
 */
export function addPublicVisualizationInitialValues(
  params: VisualBuilderDataSelectorParams,
): (dispatch: ThunkDispatch, getState: () => State) => Promise<void> {
  return async (dispatch: ThunkDispatch, getState: () => State) => {
    const { tabStatus } = params;
    const { mode } = getVisualBuilderMode(params) as VisualBuilderModeResult;
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { property } = currentPropertySelector(getState() as any);

    const { currentMediaItemId, filteredMediaItems = [], gridView, mediaItems = [], scope } = visualBuilderData;

    // Check if we're in a public visualization mode
    if (
      !(
        mode === VB_MODE.PUBLIC_VISUALIZATION ||
        mode === VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION ||
        mode === VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION
      )
    ) {
      return;
    }

    // Set initial drawer options and values
    const values: {
      leftDrawerAutoHide: boolean;
      rightDrawerAutoHide: boolean;
      leftDrawer?: boolean;
      typeFilterId?: number;
      assetCategoryFilterId?: number;
      primaryFilter?: string;
    } = {
      ...(getDrawerOptions({ tabStatus, mediaItems, scope }) as {
        leftDrawerAutoHide: boolean;
        rightDrawerAutoHide: boolean;
        leftDrawer?: boolean;
      }),
    };

    let newCurrentMediaItemId = currentMediaItemId;

    // Handle asset parameter from URL - this takes absolute priority
    // When ?asset={mediaItemId} is in the URL, set currentMediaItemId to that value
    if (params.asset && !gridView && property?.publicViewer) {
      const assetNumber = typeof params.asset === "string" ? parseInt(params.asset, 10) : params.asset;
      let targetMediaItem: Asset | undefined;

      // For all-assets-share, check filteredMediaItems first (what's actually visible)
      // For experience shares, check mediaItems (all items in the experience)
      if (tabStatus === TAB_STATUSES.PUBLIC_MEDIA_SHARE) {
        targetMediaItem =
          filteredMediaItems.find((item) => item.id === assetNumber) ||
          mediaItems.find((item) => item.id === assetNumber);

        // If asset is in mediaItems but not in filteredMediaItems, clear filters
        if (!filteredMediaItems.find((item) => item.id === assetNumber) && targetMediaItem) {
          values.typeFilterId = undefined;
          values.assetCategoryFilterId = undefined;
          values.primaryFilter = EXPERIENCE_PRIMARY_FILTER.TAG;
        }
      } else if (tabStatus === TAB_STATUSES.PUBLIC) {
        targetMediaItem = mediaItems.find((item) => item.id === assetNumber);
      }

      // If we found the asset, always use it (even if it means overriding currentMediaItemId)
      if (targetMediaItem) {
        newCurrentMediaItemId = targetMediaItem.id;
      } else {
        // Asset not found in current lists, but params.asset exists - try to find it in mediaItems
        // This handles cases where the asset exists but filters need to be cleared first
        const assetInAllItems = mediaItems.find((item) => item.id === assetNumber);
        if (assetInAllItems) {
          // Clear filters so the asset becomes available
          if (tabStatus === TAB_STATUSES.PUBLIC_MEDIA_SHARE) {
            values.typeFilterId = undefined;
            values.assetCategoryFilterId = undefined;
            values.primaryFilter = EXPERIENCE_PRIMARY_FILTER.TAG;
          }
          newCurrentMediaItemId = assetInAllItems.id;
        }
        // If still not found, we keep newCurrentMediaItemId as is (won't update)
      }
    } else if (!params.asset) {
      // Set initial media item for public tab (only if no asset parameter)
      // Don't override if currentMediaItemId is already set (could be from share options)
      if (tabStatus === TAB_STATUSES.PUBLIC && filteredMediaItems.length && !gridView && !currentMediaItemId) {
        const initialMediaItem = filteredMediaItems[0];

        if (initialMediaItem) {
          newCurrentMediaItemId = initialMediaItem.id;
        }
      }
    }

    // Determine if we need to refresh after clearing filters
    const needsRefresh = !!(
      params.asset &&
      tabStatus === TAB_STATUSES.PUBLIC_MEDIA_SHARE &&
      (values.typeFilterId === undefined || values.assetCategoryFilterId === undefined)
    );

    // Update if values changed or currentMediaItemId changed
    if (Object.keys(values).length || newCurrentMediaItemId !== currentMediaItemId) {
      dispatch(
        updateVisualBuilderData(params, { ...values, currentMediaItemId: newCurrentMediaItemId }, {}, needsRefresh),
      );
    }
  };
}

export function buildTransformedPayload(payload: Payload) {
  const transformedContentTypesPayload = {
    ...payload,
    ...transformContentTypesData({
      contentTypes: payload.contentTypes,
      contentTypesOrdered: payload.contentTypesOrdered,
      propertyId: payload.propertyId,
    }),
  };

  const transformedTagsPayload = {
    ...transformedContentTypesPayload,
    ...transformTagsData({
      hiddenTags: transformedContentTypesPayload.hiddenTags,
      assetCategoriesOrdered: transformedContentTypesPayload.assetCategoriesOrdered,
      assetCategories: transformedContentTypesPayload.assetCategories,
      contentTypes: transformedContentTypesPayload.contentTypes,
      tags: transformedContentTypesPayload.tags,
      tagsOrdered: transformedContentTypesPayload.tagsOrdered,
    }),
  };

  return {
    ...transformedTagsPayload,
    ...transformMediaItemData({
      mediaItems: transformedTagsPayload.mediaItems,
      contentTypes: transformedTagsPayload.contentTypes,
      tags: transformedTagsPayload.tags,
      contentTypeTags: transformedTagsPayload.contentTypeTags,
      tagContentTypes: transformedTagsPayload.tagContentTypes,
      assetCategories: transformedTagsPayload.assetCategories,
      assetCategoryContentTypes: transformedTagsPayload.assetCategoryContentTypes,
      contentTypeAssetCategories: transformedTagsPayload.contentTypeAssetCategories,
      hiddenTags: transformedTagsPayload.hiddenTags,
      otherTag: transformedTagsPayload.otherTag,
      tabStatus: transformedTagsPayload.tabStatus,
      hiddenContentTypes: transformedTagsPayload.hiddenContentTypes,
      hiddenAssetCategories: transformedTagsPayload.hiddenAssetCategories,
      propertyId: transformedTagsPayload.propertyId,
      primaryFilter: transformedTagsPayload.primaryFilter,
    }),
  };
}

export function buildSortedPayload(payload: Payload) {
  const sortedContentTypePayload = {
    ...payload,
    ...getSortedContentTypes({
      contentTypes: payload.contentTypes,
      contentTypesOrdered: payload.contentTypesOrdered,
    }),
  };

  const sortedTagPayload = {
    ...sortedContentTypePayload,
    ...getSortedTags({
      tags: sortedContentTypePayload.tags,
      tagsOrdered: sortedContentTypePayload.tagsOrdered,
      assetCategories: sortedContentTypePayload.assetCategories,
      assetCategoriesOrdered: sortedContentTypePayload.assetCategoriesOrdered,
    }),
  };

  return {
    ...sortedTagPayload,
    ...getSortedMediaItems({
      typeFilterId: sortedTagPayload.typeFilterId,
      assetCategoryFilterId: sortedTagPayload.assetCategoryFilterId,
      mediaItems: sortedTagPayload.mediaItems,
      experienceAssets: sortedTagPayload.experienceAssets,
    }),
  };
}

export function buildFilteredPayload(
  payload: Payload & {
    otherTag?: Tag;
    propertyMenuItems?: MenuItem[];
    tabStatus?: string;
    contentTypeAssetCategories?: Record<string, number[]>;
    assetCategoryTabData?: {
      mediaItems: Asset[];
      assetCategoryHiddenMediaItems: Asset[];
      contentTypeHiddenMediaItems: Asset[];
    };
    contentTypeTabData?: {
      mediaItems: Asset[];
      assetCategoryHiddenMediaItems: Asset[];
      contentTypeHiddenMediaItems: Asset[];
    };
  },
) {
  const filteredMediaItemsPayload = {
    ...payload,
    ...getVisualBuilderFilteredMediaItems({
      mediaItems: payload.mediaItems,
      typeFilterId: payload.typeFilterId,
      assetCategoryFilterId: payload.assetCategoryFilterId,
      search: payload.search,
      otherTag: payload.otherTag,
      primaryFilter: payload.primaryFilter,
      propertyMenuItems: payload.propertyMenuItems,
      currentMediaItemId: payload.currentMediaItemId,
      assetCategoryHiddenMediaItems: payload.assetCategoryHiddenMediaItems,
      contentTypeHiddenMediaItems: payload.contentTypeHiddenMediaItems,
    }),
  };

  const filteredContentTypesPayload = {
    ...filteredMediaItemsPayload,
    ...getVisualBuilderFilteredContentTypes({
      validContentTypes: filteredMediaItemsPayload.validContentTypes,
      searchPerformed: filteredMediaItemsPayload.searchPerformed,
      hiddenContentTypes: filteredMediaItemsPayload.hiddenContentTypes,
      contentTypesOrdered: filteredMediaItemsPayload.contentTypesOrdered,
      contentTypes: filteredMediaItemsPayload.contentTypes,
      tabStatus: filteredMediaItemsPayload.tabStatus,
    }),
  };

  return {
    ...filteredContentTypesPayload,
    ...getVisualBuilderFilteredTags({
      primaryFilter: filteredContentTypesPayload.primaryFilter,
      typeFilterId: filteredContentTypesPayload.typeFilterId,
      contentTypeTags: filteredContentTypesPayload.contentTypeTags,
      validTags: filteredContentTypesPayload.validTags,
      hiddenTags: filteredContentTypesPayload.hiddenTags,
      searchPerformed: filteredContentTypesPayload.searchPerformed,
      tagsOrdered: filteredContentTypesPayload.tagsOrdered,
      tagNames: filteredContentTypesPayload.tagNames,
      filteredTagMediaItems: filteredContentTypesPayload.filteredTagMediaItems,
      filteredAssetCategoryMediaItems: filteredContentTypesPayload.filteredAssetCategoryMediaItems,
      assetCategoriesOrdered: filteredContentTypesPayload.assetCategoriesOrdered,
      contentTypeAssetCategories: filteredContentTypesPayload.contentTypeAssetCategories,
      assetCategoryNames: filteredContentTypesPayload.assetCategoryNames,
      tabStatus: filteredContentTypesPayload.tabStatus,
      tags: filteredContentTypesPayload.tags,
      assetCategories: filteredContentTypesPayload.assetCategories,
      currentMediaItemId: filteredContentTypesPayload.currentMediaItemId,
      shouldToggleTagItem: filteredContentTypesPayload.shouldToggleTagItem,
      mediaItems: filteredContentTypesPayload.mediaItems,
      tagsToggled: filteredContentTypesPayload.tagsToggled,
    }),
  };
}

interface GetInitialSelectionParams {
  mediaItems: Asset[];
  contentTypes: ContentType[];
  assetCategories: AssetCategory[];
  propertyMenuItems: MenuItem[];
}

/**
 * Determines the initial selection for presentation mode in the Visual Builder.
 * Selects the first Asset Category → first Content Type within it → first Media Item matching both,
 * all based on property menu item order.
 */
function getInitialPresentationSelection({
  mediaItems,
  contentTypes,
  assetCategories,
  propertyMenuItems,
}: GetInitialSelectionParams): {
  initialCategory?: AssetCategory;
  initialContentType?: ContentType;
  initialMediaItem?: Asset;
} {
  const sortedACs = sortMenuItems({
    parentId: 0,
    itemType: "asset-category",
    items: assetCategories,
    menuItems: propertyMenuItems,
  }) as AssetCategory[];

  const initialCategory = sortedACs[0];
  if (!initialCategory) return {};

  const mediaInAC = mediaItems.filter((m) => m.assetCategoryId === initialCategory.id);
  const contentTypeIds = [...new Set(mediaInAC.map((m) => m.contentTypeId))];

  const candidateCTs = contentTypes.filter((ct) => contentTypeIds.includes(ct.id));

  const sortedCTs = sortMenuItems({
    parentId: initialCategory.id,
    itemType: "content-type",
    items: candidateCTs,
    menuItems: propertyMenuItems,
  }) as ContentType[];

  const initialContentType = sortedCTs[0];
  if (!initialContentType) return { initialCategory };

  const mediaInCT = mediaInAC.filter((m) => m.contentTypeId === initialContentType.id);

  const sortedMIs = sortMenuItems({
    parentId: initialContentType.id,
    itemType: "media-item",
    items: mediaInCT,
    menuItems: propertyMenuItems,
  }) as Asset[];

  const initialMediaItem = sortedMIs[0];

  return {
    initialCategory,
    initialContentType,
    initialMediaItem,
  };
}
